<script lang="ts">
export default {
    name: 'BaseIconWidget',
};
</script>
<script lang="ts" setup>
import Back from '@pet/base.back/index.vue';
import YodaScan from '@pet/base.scan/index.vue';
import type { ScanScene, ScanConfig, OpenTarget, ScanResult } from '@pet/base.scan/index.vue';
import { computed } from 'vue-demi';

import BackIcon from './icons/back.vue';
import Scan from './icons/scan.vue';
import type { WidgetType } from './types';

type TextType = Exclude<WidgetType, 'scan' | 'back' | 'text'>;

interface Props {
    /**
     * 挂件类型
     */
    type?: WidgetType;
    /**
     * 挂件文案
     */
    text?: string;
    /**
     * 返回前执行
     */
    beforeBack?: (() => void) | (() => Promise<void>);
    /**
     * 自定义返回
     */
    customBack?: (() => void) | (() => Promise<void>);
    /**
     * 图标颜色
     */
    color?: string;
    /**
     * 图标背景
     */
    background?: string;
    /**
     * scanScene
     */
    scanScene?: ScanScene;
    /**
     * 扫码配置
     */
    scanConfig?: ScanConfig;
    /**
     * 扫描打开方式
     */
    scanTarget?: OpenTarget;
    /**
     * 扫描出错时的兜底文案
     */
    scanErrMsg?: string;
}

const map: Record<TextType, string> = {
    share: '分享',
    rule: '规则',
    wallet: '钱包',
    more: '更多',
};

const emit = defineEmits<{
    (e: 'click', params?: string): void;
    (e: 'scanResult', val?: ScanResult): void;
}>();

const props = withDefaults(defineProps<Props>(), {
    type: 'back',
    text: '',
});

const renderText = computed(() => map[props.type as TextType] ?? props.text);

const iconColor = computed(() => {
    return Boolean(props.color)
        ? {
              color: props.color,
          }
        : {};
});
const backgroundColor = computed(() => {
    return Boolean(props.background)
        ? {
              background: props.background,
          }
        : {};
});

async function clickHandler() {
    emit('click', props.type);
}

function handleScanResult(res: ScanResult) {
    emit('scanResult', res);
}
</script>

<template>
    <span v-if="type === 'scan'" @click.stop="clickHandler()">
        <YodaScan
            class="icon-widget"
            :config="scanConfig"
            :target="scanTarget"
            :err-msg="scanErrMsg"
            @result="handleScanResult"
        >
            <Scan class="icon-widget-icon" />
        </YodaScan>
    </span>
    <span v-else class="icon-widget" :style="[iconColor, backgroundColor]" @click.stop="clickHandler()">
        <Back v-if="type === 'back'" class="icon-widget-icon" :before-back="beforeBack" :custom-back="customBack">
            <BackIcon class="icon-widget-icon" />
        </Back>
        <template v-else>
            {{ renderText }}
            <slot />
        </template>
    </span>
</template>

<style lang="scss" scoped>
$widget-color: var(--base-icon-widget-color, #fff);
$widget-background-color: var(--base-icon-widget-background, rgba(#000, 0.2));
$widget-size: 32px;
$widget-font-size: 10px;

.icon-widget {
    display: inline-flex;
    justify-content: center;
    align-items: center;
    vertical-align: middle;
    cursor: pointer;
    background-color: $widget-background-color;
    width: $widget-size;
    height: $widget-size;
    border-radius: 50%;
    color: $widget-color;
    font-size: $widget-font-size;
    font-weight: 700;
    box-sizing: border-box;
    -webkit-tap-highlight-color: transparent;
}
.icon-widget-icon {
    width: 100%;
    height: 100%;
}
</style>
